/*
  CallsCharts.tsx

  This file contains the CallsCharts component, which is used to display the charts for the calls page.
*/
import {GridFilterModel, GridSortModel} from '@mui/x-data-grid-pro';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';

import {Button} from '../../../../../components/Button';
import {Tailwind} from '../../../../../components/Tailwind';
import {Select} from '../../../../Form/Select';
import {WFHighLevelCallFilter} from '../pages/CallsPage/callsTableFilter';
import {ChartConfig} from '../pages/wfReactInterface/generatedBuiltinObjectClasses.zod';
import {callsChartsStyles} from './CallsCharts.styles';
import {Chart} from './Chart';
import {ChartModal} from './ChartModal';
import {
  ChartsProvider,
  useChartsDispatch,
  useChartsState,
} from './ChartsContext';
import {ChartTypeSelectionDrawer} from './ChartTypeSelectionDrawer';
import {EvalsSummary} from './EvalsSummary';
import {chartAxisFields} from './extractData';
import {InternalChartConfig} from './types';
import {useChartsData} from './useChartsData';

type CallsChartsProps = {
  entity: string;
  project: string;
  filterModelProp: GridFilterModel;
  filter: WFHighLevelCallFilter;
  sortModel?: GridSortModel;
  charts?: ChartConfig[] | null;
  setCharts?: (newCharts: ChartConfig[] | null) => void;
  currentViewId?: string;
  expandRefCols?: Set<string>;
  isEvaluationPage?: boolean;
};

type PageSizeOption = {
  readonly value: number;
  readonly label: string;
};

const CallsChartsInner = ({
  entity,
  project,
  filter,
  filterModelProp,
  sortModel,
  expandRefCols,
  isEvaluationPage,
}: CallsChartsProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(
    isEvaluationPage ? true : false
  );
  React.useEffect(() => {
    setIsCollapsed(isEvaluationPage ? true : false);
  }, [isEvaluationPage]);
  const [pageSize, setPageSize] = React.useState(250);

  const pageSizeOptions = React.useMemo(
    () => [
      {value: 10, label: '10'},
      {value: 25, label: '25'},
      {value: 50, label: '50'},
      {value: 100, label: '100'},
      {value: 250, label: '250'},
      {value: 500, label: '500'},
      {value: 1000, label: '1000'},
    ],
    []
  );

  const pageSizeValue = pageSizeOptions.find(o => o.value === pageSize);
  const onPageSizeChange = (option: PageSizeOption | null) => {
    if (option) {
      setPageSize(option.value);
    }
  };

  const {data: callData, isLoading} = useChartsData({
    entity,
    project,
    filter,
    filterModelProp,
    pageSize,
    sortModel,
    expandRefCols,
    skip: isCollapsed,
  });
  const {charts} = useChartsState();
  const dispatch = useChartsDispatch();

  const [typeSelectionDrawerOpen, setTypeSelectionDrawerOpen] =
    React.useState(false);
  const [pendingChartTypeSelection, setPendingChartTypeSelection] =
    React.useState<'scatter' | 'line' | 'bar' | null>(null);
  const [modalState, setModalState] = React.useState<
    | {open: false}
    | {
        open: true;
        mode: 'edit';
        initialConfig: Partial<InternalChartConfig>;
        editIndex: number;
      }
  >({open: false});

  const defaultConfig = React.useMemo(
    () => ({
      x_axis: chartAxisFields[0]?.key || 'started_at',
      y_axis: chartAxisFields.find(f => f.type === 'number')?.key || 'latency',
      plot_type: 'scatter' as const,
      bin_count: 20,
      aggregation: 'average' as const,
    }),
    []
  );

  const openTypeSelectionDrawer = () => {
    setTypeSelectionDrawerOpen(true);
  };

  const closeTypeSelectionDrawer = () => {
    setTypeSelectionDrawerOpen(false);
  };

  const handleChartTypeSelection = (plotType: 'scatter' | 'line' | 'bar') => {
    const newChartConfig = {
      ...defaultConfig,
      plot_type: plotType,
    };
    setPendingChartTypeSelection(plotType);
    dispatch({
      type: 'ADD_CHART',
      payload: newChartConfig,
    });
    closeTypeSelectionDrawer();
  };

  const openEditModal = (id: string) => {
    const index = charts.findIndex(c => c.id === id);
    if (index !== -1) {
      const chart = charts[index];
      setModalState({
        open: true,
        mode: 'edit',
        initialConfig: {...chart},
        editIndex: index,
      });
    }
  };

  const closeModal = () => {
    setModalState({open: false});
  };

  const handleConfirm = (config: Partial<InternalChartConfig>) => {
    if (
      modalState.open &&
      modalState.mode === 'edit' &&
      typeof modalState.editIndex === 'number'
    ) {
      dispatch({
        type: 'UPDATE_CHART_BY_INDEX',
        index: modalState.editIndex,
        payload: config,
      });
    }
    closeModal();
  };

  const prevChartIdsRef = React.useRef(new Set(charts.map(c => c.id)));
  React.useEffect(() => {
    if (pendingChartTypeSelection && charts.length > 0) {
      const newChartIndex = charts.findIndex(
        chart =>
          !prevChartIdsRef.current.has(chart.id) &&
          chart.plot_type === pendingChartTypeSelection
      );
      if (newChartIndex !== -1) {
        setPendingChartTypeSelection(null);
        setModalState({
          open: true,
          mode: 'edit',
          initialConfig: {...charts[newChartIndex]},
          editIndex: newChartIndex,
        });
      }
    }
    prevChartIdsRef.current = new Set(charts.map(c => c.id));
  }, [charts, pendingChartTypeSelection]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleDragEnd = (result: DropResult) => {
    // Manually clean up any transform styles left by react-beautiful-dnd
    // Use a small delay to ensure react-beautiful-dnd has finished its updates
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (containerRef.current) {
          // Find all draggable elements
          const draggables = containerRef.current.querySelectorAll(
            '[data-rbd-draggable-id]'
          );
          draggables.forEach(el => {
            const htmlEl = el as HTMLElement;
            // Force reset all drag-related styles
            htmlEl.style.removeProperty('transform');
            htmlEl.style.removeProperty('z-index');
            htmlEl.style.removeProperty('pointer-events');

            // If position was set to fixed, reset it
            if (htmlEl.style.position === 'fixed') {
              htmlEl.style.removeProperty('position');
              htmlEl.style.removeProperty('top');
              htmlEl.style.removeProperty('left');
              htmlEl.style.removeProperty('width');
              htmlEl.style.removeProperty('height');
            }
          });

          // Also clean up any chart containers specifically
          const chartContainers = containerRef.current.querySelectorAll(
            '[style*="transform"]'
          );
          chartContainers.forEach(el => {
            const htmlEl = el as HTMLElement;
            if (
              htmlEl.style.transform &&
              !htmlEl.style.transform.includes('none')
            ) {
              htmlEl.style.removeProperty('transform');
            }
          });
        }
      }, 50);
    });

    if (!result.destination) {
      return;
    }

    dispatch({
      type: 'REORDER_CHARTS',
      sourceIndex: result.source.index,
      destinationIndex: result.destination.index,
    });
  };

  return (
    <Tailwind>
      <div ref={containerRef} className={callsChartsStyles.container}>
        {/* Header */}
        <div className={callsChartsStyles.header}>
          <div className={callsChartsStyles.headerContent}>
            <div className={callsChartsStyles.headerLeft}>
              <Button
                variant="ghost"
                size="small"
                icon={isCollapsed ? 'chevron-next' : 'chevron-down'}
                tooltip={isCollapsed ? 'Expand charts' : 'Collapse charts'}
                onClick={() => setIsCollapsed(prev => !prev)}
              />
              <div className={callsChartsStyles.headerText}>Charts</div>
              <div className={callsChartsStyles.headerCount}>
                {charts.length}
              </div>
              {!isCollapsed && (
                <>
                  <span className={callsChartsStyles.headerSubtext}>
                    showing data for
                  </span>
                  <Select<PageSizeOption>
                    size="small"
                    menuPlacement="bottom"
                    options={pageSizeOptions}
                    value={pageSizeValue}
                    isSearchable={false}
                    onChange={onPageSizeChange}
                  />
                  <span className={callsChartsStyles.headerSubtext}>calls</span>
                </>
              )}
            </div>
            {!isCollapsed && (
              <Button
                icon="add-new"
                variant="ghost"
                size="small"
                onClick={openTypeSelectionDrawer}>
                Add Chart
              </Button>
            )}
          </div>
        </div>

        {/* Scrollable charts container */}
        {!isCollapsed && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="charts-droppable" direction="vertical">
              {(
                provided: DroppableProvided,
                snapshot: DroppableStateSnapshot
              ) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`flex flex-col gap-0 overflow-x-hidden overflow-y-visible py-12 px-16 ${
                    snapshot.isDraggingOver ? 'bg-[#f5f5f5]' : ''
                  }`}>
                  {charts.length === 0 ? (
                    <div className={callsChartsStyles.emptyState}>
                      No charts available. Click "Add Chart" to create one.
                    </div>
                  ) : (
                    charts.map((chart, index) => {
                      const yField = chartAxisFields.find(
                        f => f.key === chart.y_axis
                      );
                      const baseTitle = yField ? yField.label : chart.y_axis;
                      const chartTitle = baseTitle;
                      return (
                        <Draggable
                          key={chart.id}
                          draggableId={chart.id}
                          index={index}>
                          {(
                            provided: DraggableProvided,
                            snapshot: DraggableStateSnapshot
                          ) => {
                            return (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={
                                  provided.draggableProps
                                    .style as React.CSSProperties
                                }
                                className={`${
                                  index === charts.length - 1 ? '' : 'mb-12'
                                } ${snapshot.isDragging ? 'opacity-80' : ''}`}>
                                <Chart
                                  data={callData}
                                  height={260}
                                  xAxis={chart.x_axis}
                                  yAxis={chart.y_axis}
                                  plotType={chart.plot_type || 'scatter'}
                                  binCount={chart.bin_count ?? undefined}
                                  aggregation={chart.aggregation ?? undefined}
                                  title={chartTitle}
                                  customName={chart.custom_name ?? undefined}
                                  chartId={chart.id}
                                  entity={entity}
                                  project={project}
                                  groupKeys={chart.group_keys ?? undefined}
                                  isLoading={isLoading}
                                  onEdit={() => openEditModal(chart.id)}
                                  onRemove={() =>
                                    dispatch({
                                      type: 'REMOVE_CHART',
                                      id: chart.id,
                                    })
                                  }
                                  filter={filter}
                                  dragHandleProps={provided.dragHandleProps}
                                />
                              </div>
                            );
                          }}
                        </Draggable>
                      );
                    })
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Evals summary only on evaluations page */}
        {isEvaluationPage ? <EvalsSummary /> : null}

        <ChartTypeSelectionDrawer
          open={typeSelectionDrawerOpen}
          onClose={closeTypeSelectionDrawer}
          onSelectType={handleChartTypeSelection}
        />

        {modalState.open && (
          <ChartModal
            open={modalState.open}
            mode="edit"
            initialConfig={modalState.initialConfig}
            onClose={closeModal}
            onConfirm={handleConfirm}
            callData={callData}
            entity={entity}
            project={project}
          />
        )}
      </div>
    </Tailwind>
  );
};

export const CallsCharts = (props: CallsChartsProps) => (
  <ChartsProvider
    entity={props.entity}
    project={props.project}
    charts={props.charts}
    setCharts={props.setCharts}
    currentViewId={props.currentViewId}>
    <CallsChartsInner {...props} />
  </ChartsProvider>
);
